import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, dateFr, DEVIS_STATUS } from "@/lib/constants";
import { generatePdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Copy, FileDown, ReceiptEuro, Trash2, Eye } from "lucide-react";

const FILTERS = [
  { key: "", label: "Tous" },
  { key: "brouillon", label: "Brouillon" },
  { key: "envoye", label: "Envoyé" },
  { key: "accepte", label: "Accepté" },
  { key: "refuse", label: "Refusé" },
];

export default function DevisList() {
  const [devis, setDevis] = useState([]);
  const [filter, setFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();

  const load = useCallback(async (status = "") => {
    const { data } = await api.get("/devis", { params: status ? { status } : {} });
    setDevis(data);
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/devis/${id}/duplicate`);
      toast.success(`Devis dupliqué : ${data.number}`);
      load(filter);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const convert = async (id) => {
    try {
      const { data } = await api.post(`/devis/${id}/convert`);
      toast.success(`Facture créée : ${data.number}`);
      navigate(`/factures/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/devis/${deleteId}`);
      toast.success("Devis supprimé");
      setDeleteId(null);
      load(filter);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pdf = (d) => generatePdf(d, settings, "devis");

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Devis</h1>
          <p className="text-slate-600">{devis.length} devis</p>
        </div>
        <Button data-testid="add-devis-button" onClick={() => navigate("/devis/new")} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">
          <Plus size={20} className="mr-1" /> Nouveau devis
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`devis-filter-${f.key || "all"}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
              filter === f.key ? "bg-[#0284C7] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {devis.length === 0 && <p className="text-slate-500">Aucun devis.</p>}
        {devis.map((d) => (
          <div key={d.id} data-testid={`devis-row-${d.id}`} className="bg-white border border-slate-200 rounded-md p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading font-bold text-lg text-slate-900">{d.number}</span>
                <span className={`text-xs font-semibold px-2 py-1 rounded ${DEVIS_STATUS[d.status]?.cls}`}>{DEVIS_STATUS[d.status]?.label}</span>
                {d.facture_id && <span className="text-xs font-semibold px-2 py-1 rounded bg-green-100 text-green-700">Facturé</span>}
              </div>
              <div className="text-sm text-slate-600 mt-1 truncate">
                {d.client?.name || "—"} · {d.chantier_type} · {dateFr(d.date)}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-slate-500">Total TTC</div>
              <div className="font-heading font-extrabold text-xl text-[#0284C7]">{eur(d.total_ttc)}</div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button size="sm" variant="outline" data-testid={`open-devis-${d.id}`} onClick={() => navigate(`/devis/${d.id}`)} className="border-slate-300"><Eye size={16} className="mr-1" />Ouvrir</Button>
              <Button size="sm" variant="outline" data-testid={`pdf-devis-${d.id}`} onClick={() => pdf(d)} className="border-slate-300"><FileDown size={16} /></Button>
              <Button size="sm" variant="outline" data-testid={`duplicate-devis-${d.id}`} onClick={() => duplicate(d.id)} className="border-slate-300"><Copy size={16} /></Button>
              {!d.facture_id && (
                <Button size="sm" data-testid={`convert-devis-${d.id}`} onClick={() => convert(d.id)} className="bg-[#16A34A] hover:bg-[#15803D]"><ReceiptEuro size={16} className="mr-1" />Facturer</Button>
              )}
              <Button size="sm" variant="outline" data-testid={`delete-devis-${d.id}`} onClick={() => setDeleteId(d.id)} className="border-red-300 text-red-600"><Trash2 size={16} /></Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce devis ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-devis" onClick={remove} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
